import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { authDiagnostics } from '../utils/authDiagnostics';

const AuthDiagnostics = ({ open, onClose }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [testResults, setTestResults] = useState({});

  const runFullDiagnostic = async () => {
    setLoading(true);
    setTestResults({});
    
    try {
      const diagnostic = await authDiagnostics.runFullDiagnostic();
      setResults(diagnostic);
      
      // Run individual tests
      const tests = {
        connection: diagnostic.connection,
        session: diagnostic.session,
        twilio: await authDiagnostics.testTwilioWebhook()
      };
      
      setTestResults(tests);
    } catch (error) {
      console.error('Diagnostic error:', error);
      setResults({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const testOtpSend = async () => {
    if (!phone) {
      alert('Please enter a phone number');
      return;
    }
    
    setLoading(true);
    try {
      const result = await authDiagnostics.testOtpSend(phone);
      setTestResults(prev => ({ ...prev, otpSend: result }));
    } finally {
      setLoading(false);
    }
  };

  const testOtpVerify = async () => {
    if (!phone || !otp) {
      alert('Please enter phone and OTP code');
      return;
    }
    
    setLoading(true);
    try {
      const result = await authDiagnostics.testOtpVerify(phone, otp);
      setTestResults(prev => ({ ...prev, otpVerify: result }));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (success) => {
    if (success === true) return <CheckCircleIcon color="success" />;
    if (success === false) return <ErrorIcon color="error" />;
    return <InfoIcon color="info" />;
  };

  const getStatusColor = (success) => {
    if (success === true) return 'success';
    if (success === false) return 'error';
    return 'info';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Authentication Diagnostics
        <Typography variant="caption" display="block" color="textSecondary">
          Comprehensive testing of authentication chain
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Quick Actions */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quick Tests</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={runFullDiagnostic}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                Run Full Diagnostic
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => authDiagnostics.testConnection()}
              >
                Test Database
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => authDiagnostics.testAuthSession()}
              >
                Check Session
              </Button>
            </Box>
          </Paper>

          {/* OTP Testing */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>OTP Testing</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="5083407588"
                size="small"
                sx={{ flex: 1 }}
              />
              <Button 
                variant="outlined" 
                onClick={testOtpSend}
                disabled={loading || !phone}
              >
                Send OTP
              </Button>
            </Box>
            
            {testResults.otpSend && (
              <Alert 
                severity={testResults.otpSend.success ? 'success' : 'error'}
                sx={{ mt: 1 }}
              >
                {testResults.otpSend.success 
                  ? `OTP sent in ${testResults.otpSend.responseTime}ms`
                  : testResults.otpSend.error
                }
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-end' }}>
              <TextField
                label="OTP Code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                size="small"
                sx={{ flex: 1 }}
              />
              <Button 
                variant="outlined" 
                onClick={testOtpVerify}
                disabled={loading || !phone || !otp}
              >
                Verify OTP
              </Button>
            </Box>
            
            {testResults.otpVerify && (
              <Alert 
                severity={testResults.otpVerify.success ? 'success' : 'error'}
                sx={{ mt: 1 }}
              >
                {testResults.otpVerify.success 
                  ? `Verified via ${testResults.otpVerify.method} in ${testResults.otpVerify.responseTime}ms`
                  : testResults.otpVerify.error
                }
              </Alert>
            )}
          </Paper>

          {/* Test Results */}
          {Object.keys(testResults).length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Test Results</Typography>
              <List dense>
                {testResults.connection && (
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(testResults.connection.success)}
                    </ListItemIcon>
                    <ListItemText
                      primary="Database Connection"
                      secondary={testResults.connection.success 
                        ? `Connected in ${testResults.connection.responseTime}ms`
                        : testResults.connection.error
                      }
                    />
                    <Chip 
                      label={testResults.connection.success ? 'PASS' : 'FAIL'}
                      color={getStatusColor(testResults.connection.success)}
                      size="small"
                    />
                  </ListItem>
                )}
                
                {testResults.session && (
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(testResults.session.success)}
                    </ListItemIcon>
                    <ListItemText
                      primary="Auth Session"
                      secondary={testResults.session.session 
                        ? `Active session for user ${testResults.session.session.userId}`
                        : 'No active session'
                      }
                    />
                    <Chip 
                      label={testResults.session.session ? 'ACTIVE' : 'NONE'}
                      color={testResults.session.session ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                )}
                
                {testResults.twilio && (
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(testResults.twilio.success)}
                    </ListItemIcon>
                    <ListItemText
                      primary="Twilio SMS Provider"
                      secondary={testResults.twilio.message || testResults.twilio.error}
                    />
                    <Chip 
                      label={testResults.twilio.success ? 'CONFIGURED' : 'ERROR'}
                      color={getStatusColor(testResults.twilio.success)}
                      size="small"
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}

          {/* Full Diagnostic Results */}
          {results && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Full Diagnostic Report</Typography>
              <Box sx={{ 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto'
              }}>
                <pre style={{ margin: 0, fontSize: '0.85rem' }}>
                  {JSON.stringify(results, null, 2)}
                </pre>
              </Box>
            </Paper>
          )}

          {/* Console Instructions */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Console Access:</strong> Open browser console and use:
            </Typography>
            <Box component="code" sx={{ display: 'block', mt: 1 }}>
              authDiag.runFullDiagnostic()
            </Box>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthDiagnostics;